import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Users, FileQuestion, MapPin, MessageSquare, FileDown, Loader2, Pencil, ExternalLink } from "lucide-react";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useGenerateReport } from "@/hooks/use-reports";
import { useUpdateStudy } from "@/hooks/use-studies";
import { useAuth } from "@/auth/useAuth";
import { downloadBase64Pdf } from "@/lib/download";
import type { Study } from "@/api/types";

export function StudyInspector({ data }: { data: unknown }) {
  const study = data as Study;
  const generateReport = useGenerateReport();
  const updateStudy = useUpdateStudy();
  const { can } = useAuth();
  const canEdit = can("edc_data:write");

  const [editing, setEditing] = useState(false);
  const [editStatus, setEditStatus] = useState(study.status);
  const [editPhase, setEditPhase] = useState(study.phase ?? "");
  const [editArea, setEditArea] = useState(study.therapeuticArea ?? "");
  const [editIndication, setEditIndication] = useState(study.indication ?? "");
  const [editSponsor, setEditSponsor] = useState(study.sponsor ?? "");

  return (
    <div className="space-y-6">
      <Link
        to={`/studies/${study.id}`}
        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
      >
        <ExternalLink className="h-3 w-3" /> View Full Details
      </Link>
      <div>
        <h3 className="text-lg font-semibold text-foreground">{study.name}</h3>
        {study.protocolNumber && (
          <p className="mt-0.5 text-sm text-muted-foreground">{study.protocolNumber}</p>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          <StatusBadge status={study.status} />
          {study.phase && <StatusBadge status={study.phase} />}
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {study.edcSystem}
          </span>
        </div>
      </div>

      {!editing && (study.therapeuticArea || study.indication || study.sponsor) && (
        <div className="space-y-2">
          {study.therapeuticArea && (
            <Detail label="Therapeutic Area" value={study.therapeuticArea} />
          )}
          {study.indication && <Detail label="Indication" value={study.indication} />}
          {study.sponsor && <Detail label="Sponsor" value={study.sponsor} />}
        </div>
      )}

      {canEdit && !editing && (
        <button
          onClick={() => setEditing(true)}
          data-testid="study-edit-btn"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80"
        >
          <Pencil className="h-3 w-3" /> Edit Details
        </button>
      )}

      {editing && (
        <div className="space-y-3 border-t border-border pt-4" data-testid="study-edit-form">
          <EditField label="Status" value={editStatus} onChange={setEditStatus} type="select"
            options={["active", "completed", "closed", "paused"]} />
          <EditField label="Phase" value={editPhase} onChange={setEditPhase} type="select"
            options={["", "Phase I", "Phase II", "Phase III", "Phase IV"]} />
          <EditField label="Therapeutic Area" value={editArea} onChange={setEditArea} />
          <EditField label="Indication" value={editIndication} onChange={setEditIndication} />
          <EditField label="Sponsor" value={editSponsor} onChange={setEditSponsor} />
          <div className="flex gap-2">
            <button
              onClick={() => {
                updateStudy.mutate(
                  {
                    studyId: study.id,
                    status: editStatus,
                    phase: editPhase || undefined,
                    therapeuticArea: editArea || undefined,
                    indication: editIndication || undefined,
                    sponsor: editSponsor || undefined,
                  },
                  { onSuccess: () => setEditing(false) },
                );
              }}
              disabled={updateStudy.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {updateStudy.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setEditStatus(study.status);
                setEditPhase(study.phase ?? "");
                setEditArea(study.therapeuticArea ?? "");
                setEditIndication(study.indication ?? "");
                setEditSponsor(study.sponsor ?? "");
              }}
              className="rounded-md border border-border px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {study.counts && (
        <div className="grid grid-cols-2 gap-3">
          <CountCard icon={<MapPin className="h-4 w-4" />} label="Sites" value={study.counts.sites} />
          <CountCard icon={<Users className="h-4 w-4" />} label="Subjects" value={study.counts.subjects} />
          {study.counts.forms !== undefined && (
            <CountCard icon={<Building2 className="h-4 w-4" />} label="Forms" value={study.counts.forms} />
          )}
          <CountCard icon={<FileQuestion className="h-4 w-4" />} label="Open Queries" value={study.counts.openQueries} />
        </div>
      )}

      <div className="space-y-2">
        <Link
          to={`/sites?studyId=${study.id}`}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          <MapPin className="h-4 w-4" /> View Sites
        </Link>
        <Link
          to={`/subjects?studyId=${study.id}`}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          <Users className="h-4 w-4" /> View Subjects
        </Link>
        <Link
          to={`/queries?studyId=${study.id}`}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          <FileQuestion className="h-4 w-4" /> View Queries
        </Link>
        <button
          data-testid="generate-report-btn"
          disabled={generateReport.isPending}
          onClick={() =>
            generateReport.mutate(
              { studyId: study.id },
              {
                onSuccess: (res) => downloadBase64Pdf(res.pdf, res.filename),
              }
            )
          }
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
        >
          {generateReport.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileDown className="h-4 w-4" />
          )}
          Generate Report
        </button>
        <Link
          to="/chat"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <MessageSquare className="h-4 w-4" /> Ask AI about this study
        </Link>
      </div>

      {study.lastSyncedAt && (
        <p className="text-xs text-muted-foreground">
          Last synced: {new Date(study.lastSyncedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}

function EditField({
  label, value, onChange, type = "text", options,
}: {
  label: string; value: string; onChange: (v: string) => void;
  type?: "text" | "select"; options?: string[];
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      {type === "select" && options ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        >
          {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
        </select>
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        />
      )}
    </div>
  );
}

function CountCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center gap-2 text-muted-foreground">{icon}<span className="text-xs">{label}</span></div>
      <p className="mt-1 text-xl font-semibold text-foreground">{value}</p>
    </div>
  );
}
